import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Egg, Warehouse, LogOut, ShoppingCart, Calculator, Lock, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

export const MODULES = [
  { key: "kumes", path: "/kumes-modul", title: "Kümes", desc: "Üretim, yem, ilaç, randıman, stok öngörüleri ve kümes satış (sevkiyat, depolar arası).", icon: Egg, tone: "bg-primary/10 text-primary" },
  { key: "depot", path: "/depo", title: "Ana Sevk & Paketleme Deposu", desc: "Dış tedarikçi mal girişi ve konsolide depo stok takibi (MİM KÜMES + dış firmalar).", icon: Warehouse, tone: "bg-amber-500/10 text-amber-600" },
  { key: "sales", path: "/pazarlama", title: "Pazarlama / Satış", desc: "Müşteriye hızlı satış fişi: renk, boy, stoktaki firma, karton ve fiyat.", icon: ShoppingCart, tone: "bg-green-500/10 text-green-600" },
  { key: "accounting", path: "/on-muhasebe", title: "Ön Muhasebe", desc: "Bekleyen satış fişlerini onaylayıp stoktan düşme, iptal ve takip.", icon: Calculator, tone: "bg-sky-500/10 text-sky-600" },
];

function ModuleCard({ m, allowed, onOpen }) {
  return (
    <div className="relative">
    {!allowed && (
      <span className="absolute top-4 right-4 z-10 inline-flex items-center gap-1 rounded-full bg-foreground/85 text-background px-2.5 py-1 text-[11px] font-semibold pointer-events-none" data-testid={`module-${m.key}-locked`}>
        <Lock size={12} /> Yetkiniz Yok
      </span>
    )}
    <button onClick={() => (allowed ? onOpen(m) : toast.error("Bu modüle erişim yetkiniz bulunmamaktadır"))} aria-disabled={!allowed}
      data-testid={`module-${m.key}`} data-locked={allowed ? "false" : "true"}
      className={`group w-full h-full rounded-2xl border bg-card p-8 text-left transition-[border-color,box-shadow,opacity] ${allowed ? "border-border hover:border-primary hover:shadow-xl" : "border-dashed border-border opacity-50 grayscale cursor-not-allowed"}`}>
      <div className={`h-14 w-14 rounded-xl flex items-center justify-center mb-5 transition-transform ${m.tone} ${allowed ? "group-hover:scale-105" : ""}`}><m.icon size={28} /></div>
      <div className="font-head text-2xl font-semibold mb-1">{m.title}</div>
      <p className="text-sm text-muted-foreground">{m.desc}</p>
    </button>
    </div>
  );
}

export default function ModuleSelect() {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();
  const [hideLocked, setHideLocked] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setHideLocked(!!r.data?.hide_locked_modules)).catch(() => {}); }, []);
  const list = MODULES.filter((m) => !hideLocked || hasModule(m.key)).sort((a, b) => Number(hasModule(b.key)) - Number(hasModule(a.key)));
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6 pt-safe-20" data-testid="module-select">
      <div className="absolute mt-safe-5 right-5 flex items-center gap-3 text-sm text-muted-foreground">
        <span>{user?.name || user?.email}</span>
        <ThemeToggle testid="module-theme-toggle" />
        <button onClick={logout} data-testid="module-logout" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"><LogOut size={15} /> Çıkış</button>
      </div>
      <div className="text-center mb-10">
        <h1 className="font-head text-4xl sm:text-5xl font-bold tracking-tight">MİM GIDA</h1>
        <p className="text-muted-foreground mt-2 text-base">Lütfen çalışmak istediğiniz modülü seçin</p>
      </div>
      <div className={`grid grid-cols-1 gap-6 w-full ${list.length > 1 ? "md:grid-cols-2 max-w-3xl" : "max-w-md"}`}>
        {list.map((m) => <ModuleCard key={m.key} m={m} allowed={hasModule(m.key)} onOpen={(x) => navigate(x.path)} />)}
      </div>
      <button onClick={() => navigate("/ayarlar")} data-testid="module-settings"
        className="mt-10 inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold hover:border-primary hover:text-primary transition-colors">
        <Settings size={18} className="transition-transform group-hover:rotate-45" /> Ayarlar
        <span className="text-xs font-normal text-muted-foreground">{user?.role === "admin" ? "· Sistem & Kullanıcılar" : "· Hesabım"}</span>
      </button>
    </div>
  );
}
